import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { products } from '../data/mock';

const ProductCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % products.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % products.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + products.length) % products.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '600px',
      overflow: 'hidden',
      backgroundColor: '#fff'
    }}>
      {/* Slides Container */}
      <div style={{
        display: 'flex',
        transform: `translateX(-${currentSlide * 100}%)`,
        transition: 'transform 0.8s cubic-bezier(0.645, 0.045, 0.355, 1)',
        height: '100%'
      }}>
        {products.map((product, index) => (
          <div
            key={product.id}
            style={{
              minWidth: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '80px',
              position: 'relative'
            }}
          >
            {/* Decorative Background Elements */}
            <div style={{
              position: 'absolute',
              top: '10%',
              right: '10%',
              width: '300px',
              height: '300px',
              backgroundColor: '#f6f5e8',
              borderRadius: '50%',
              opacity: 0.5,
              zIndex: 0
            }} />
            <div style={{
              position: 'absolute',
              bottom: '15%',
              left: '8%',
              width: '200px',
              height: '200px',
              backgroundColor: '#ebeade',
              borderRadius: '50%',
              opacity: 0.6,
              zIndex: 0
            }} />

            {/* Product Content */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '80px',
              alignItems: 'center',
              maxWidth: '1200px',
              width: '100%',
              zIndex: 1,
              position: 'relative'
            }}>
              {/* Product Info */}
              <div style={{
                animation: currentSlide === index ? 'fadeInLeft 0.8s ease-out' : 'none'
              }}>
                <div style={{
                  display: 'inline-block',
                  padding: '6px 16px',
                  backgroundColor: '#ebeade',
                  marginBottom: '16px',
                  borderRadius: '0px'
                }}>
                  <span className="body-small">Featured Product</span>
                </div>
                
                <h2 className="hero-large" style={{ 
                  marginBottom: '16px',
                  fontSize: '36px'
                }}>
                  {product.name}
                </h2>
                
                <p className="body-large" style={{ 
                  color: '#666666',
                  marginBottom: '24px',
                  lineHeight: '1.7'
                }}>
                  {product.description}
                </p>

                <div style={{ 
                  display: 'flex', 
                  alignItems: 'baseline', 
                  gap: '12px',
                  marginBottom: '32px'
                }}>
                  <span className="hero-medium">₹{product.price.toLocaleString()}</span>
                  <span className="body-regular" style={{ 
                    color: '#bcbbb4', 
                    textDecoration: 'line-through' 
                  }}>
                    ₹{product.originalPrice.toLocaleString()}
                  </span>
                  <span className="body-regular" style={{ 
                    color: '#ba3e2b',
                    fontWeight: '500'
                  }}>
                    {product.discount}% off
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <a href={`/product/${product.id}`} className="btn-primary">
                    View Details
                  </a>
                </div>
              </div>

              {/* Product Image */}
              <div style={{
                animation: currentSlide === index ? 'fadeInRight 0.8s ease-out' : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img 
                  src={product.image}
                  alt={product.name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '450px',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 20px 40px rgba(0, 0, 0, 0.1))'
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        style={{
          position: 'absolute',
          left: '24px',
          top: '50%',
          transform: 'translateY(-50%)',
          backgroundColor: '#fffef2',
          border: '1px solid #333333',
          width: '48px',
          height: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          borderRadius: '0px',
          zIndex: 2,
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#333333';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#fffef2';
        }}
      >
        <ChevronLeft size={24} color="#333333" />
      </button>

      <button
        onClick={nextSlide}
        style={{
          position: 'absolute',
          right: '24px',
          top: '50%',
          transform: 'translateY(-50%)',
          backgroundColor: '#fffef2',
          border: '1px solid #333333',
          width: '48px',
          height: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          borderRadius: '0px',
          zIndex: 2,
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#333333';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#fffef2';
        }}
      >
        <ChevronRight size={24} color="#333333" />
      </button>

      {/* Slide Indicators */}
      <div style={{
        position: 'absolute',
        bottom: '32px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '12px',
        zIndex: 2
      }}>
        {products.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            style={{
              width: currentSlide === index ? '40px' : '12px',
              height: '12px',
              backgroundColor: currentSlide === index ? '#333333' : '#bcbbb4',
              border: 'none',
              borderRadius: '0px',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(40px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @media (max-width: 1024px) {
          .carousel-content {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductCarousel;
